import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { InfoIcon } from '../../../icons/info';
import { cn } from '../../../lib/utils';
import { Progress } from '../progress';

/**
 * Compact completeness indicator (Figma "Profile progress card content"):
 * a round featured icon with an emoji, a one-line title with an optional
 * info button, and a thin progress bar. Track, bar and icon background share
 * one `tone`.
 *
 * Domain-agnostic: title, emoji, tone and value come from the caller.
 */
const toneVariants = cva('', {
  variants: {
    tone: {
      red: '',
      orange: '',
      blue: '',
      purple: '',
      success: '',
    },
  },
  defaultVariants: { tone: 'success' },
});

export type ProfileProgressTone = NonNullable<VariantProps<typeof toneVariants>['tone']>;

const TONE_CLASSES: Record<
  ProfileProgressTone,
  { icon: string; track: string; indicator: string }
> = {
  red: { icon: 'bg-tone-red-soft', track: 'bg-tone-red-soft', indicator: 'bg-tone-red' },
  orange: {
    icon: 'bg-tone-orange-soft',
    track: 'bg-tone-orange-soft',
    indicator: 'bg-tone-orange',
  },
  blue: { icon: 'bg-tone-blue-soft', track: 'bg-tone-blue-soft', indicator: 'bg-tone-blue' },
  purple: {
    icon: 'bg-tone-purple-soft',
    track: 'bg-tone-purple-soft',
    indicator: 'bg-tone-purple',
  },
  success: { icon: 'bg-secondary', track: 'bg-secondary', indicator: 'bg-primary' },
};

export interface ProfileProgressCardProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'title' | 'children'
> {
  /** One-line status title, e.g. "Seu currículo está incompleto". */
  title: ReactNode;
  /** Emoji shown in the featured icon. Decorative. */
  emoji: string;
  tone: ProfileProgressTone;
  /** 0–100. */
  value: number;
  /** Shows the info button when provided. */
  onInfo?: () => void;
  /** Accessible name of the info button. @default 'Mais informações' */
  infoLabel?: string;
  /**
   * Accessible name of the progress bar. Defaults to the title when it is a
   * string; pass it when the title is a node.
   */
  progressLabel?: string;
  /**
   * Root testid. Derived: info button → `<testid>-info`, progress bar →
   * `<testid>-progress`.
   */
  'data-testid'?: string;
}

const ProfileProgressCard = forwardRef<HTMLDivElement, ProfileProgressCardProps>(
  (
    {
      title,
      emoji,
      tone,
      value,
      onInfo,
      infoLabel = 'Mais informações',
      progressLabel,
      className,
      'data-testid': testId,
      ...props
    },
    ref
  ) => {
    const classes = TONE_CLASSES[tone];
    const clamped = Math.min(100, Math.max(0, value));
    const barLabel = progressLabel ?? (typeof title === 'string' ? title : undefined);

    return (
      <div
        ref={ref}
        data-slot="profile-progress-card"
        data-tone={tone}
        data-testid={testId}
        className={cn(
          'flex w-full items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm',
          toneVariants({ tone }),
          className
        )}
        {...props}
      >
        <span
          data-slot="profile-progress-card-icon"
          aria-hidden
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full text-xs leading-none',
            classes.icon
          )}
        >
          {emoji}
        </span>
        <div
          data-slot="profile-progress-card-body"
          className="flex min-w-0 flex-1 flex-col gap-1.5"
        >
          <div className="flex items-start gap-1.5">
            <span
              data-slot="profile-progress-card-title"
              className="min-w-0 flex-1 break-words text-xs font-medium leading-none text-accent-foreground"
            >
              {title}
            </span>
            {onInfo && (
              <button
                type="button"
                aria-label={infoLabel}
                onClick={onInfo}
                data-testid={testId ? `${testId}-info` : undefined}
                // 12px glyph; the pseudo-element grows the hit area to 44px
                // without moving anything around it.
                className="relative inline-flex size-3 shrink-0 items-center justify-center rounded-full text-accent-foreground before:absolute before:-inset-4 before:content-[''] hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 [&_svg]:size-3"
              >
                <InfoIcon />
              </button>
            )}
          </div>
          <Progress
            value={clamped}
            aria-label={barLabel}
            data-testid={testId ? `${testId}-progress` : undefined}
            className={cn('h-1', classes.track)}
            indicatorClassName={classes.indicator}
          />
        </div>
      </div>
    );
  }
);
ProfileProgressCard.displayName = 'ProfileProgressCard';

export { ProfileProgressCard };
